import React from "react";
import styled from "styled-components";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import Tag from "../../atoms/Tag";
import { AIReviewStatus, UserRole } from "../../../common/models/types";
import { ColorSystem } from "../../../components/design/design-system";
import { ToastManager } from "../../organisms/CommonUI/ToastPopup/ToastManager";

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
`;

const Th = styled.th<{ $withCheckbox?: boolean }>`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;

  ${(props) =>
    props.$withCheckbox
      ? `
    &:nth-child(1) { width: 4%; } /* 체크박스 */
    &:nth-child(2) { width: 6%; } /* 번호 */
    &:nth-child(3) { width: 6%; } /* 환자번호 */
    &:nth-child(4) { width: 10%; } /* 분석종류 */
    &:nth-child(5) { width: 8%; } /* 면허번호 */
    &:nth-child(6) { width: 14%; } /* 사용자 */
    &:nth-child(7) { width: 8%; } /* 상태 */
    &:nth-child(8) { width: 8%; } /* 비용 */
    &:nth-child(9) { width: 15%; } /* 분석 정보 */
    &:nth-child(10) { width: 10%; } /* 생성일 */
    &:nth-child(11) { width: 10%; } /* 액션 */
  `
      : `
    &:nth-child(1) { width: 6%; } /* 번호 */
    &:nth-child(2) { width: 8%; } /* 환자번호 */
    &:nth-child(3) { width: 10%; } /* 분석종류 */
    &:nth-child(4) { width: 6%; } /* 면허번호 */
    &:nth-child(5) { width: 16%; } /* 사용자 */
    &:nth-child(6) { width: 8%; } /* 상태 */
    &:nth-child(7) { width: 6%; } /* 비용 */
    &:nth-child(8) { width: 20%; } /* 분석 정보 */
    &:nth-child(9) { width: 10%; } /* 생성일 */
    &:nth-child(10) { width: 10%; } /* 액션 */
  `}
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusTags = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const ClickableTag = styled.div`
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid #e5e7eb;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#e5e7eb")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#374151")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageInfo = styled(Text)`
  margin: 0 16px;
  font-size: 14px;
  color: #6b7280;
`;

const CheckboxCell = styled.td`
  padding: 12px 16px;
  text-align: center;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

interface AdminAIReviewTableProps {
  reviews: any[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onRefund: (review: any) => void;
  onViewDetail: (reviewId: string) => void;
  selectedIds?: Set<number>;
  onSelectReview?: (reviewId: number, checked: boolean) => void;
  showCheckbox?: boolean;
  onRowDoubleClick?: (review: any) => void;
}

// 시간을 분:초 형식으로 변환 (밀리초 입력)
const formatDuration = (milliseconds: number | undefined): string => {
  if (!milliseconds) return "-";

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}분 ${seconds}초`;
  }
  return `${seconds}초`;
};

// AI Review 상태를 한글로 변환
const getStatusLabel = (status: number): string => {
  switch (status) {
    case -1: // FAIL
      return "실패";
    case AIReviewStatus.PENDING:
      return "대기중";
    case AIReviewStatus.READY:
      return "준비됨";
    case AIReviewStatus.PROCESSING:
    case AIReviewStatus.PROCESSING_USER_INFO:
    case AIReviewStatus.PROCESSING_PRESCRIPTION:
    case AIReviewStatus.PROCESSING_DRUG_CRAWLING:
    case AIReviewStatus.PROCESSING_DRUG_INFO:
    case AIReviewStatus.PROCESSING_PROMPT_CREATION:
    case AIReviewStatus.PROCESSING_LLM_ANALYSIS:
      return "처리중";
    case AIReviewStatus.COMPLETED:
      return "완료";
    default:
      return "알 수 없음";
  }
};

// 상태별 태그 색상
const getStatusTagColor = (status: number): "Green" | "Orange" | "Red" | "Blue" => {
  switch (status) {
    case -1: // FAIL
      return "Red";
    case AIReviewStatus.COMPLETED:
      return "Green";
    case AIReviewStatus.PENDING:
    case AIReviewStatus.READY:
      return "Orange";
    case AIReviewStatus.PROCESSING:
    case AIReviewStatus.PROCESSING_USER_INFO:
    case AIReviewStatus.PROCESSING_PRESCRIPTION:
    case AIReviewStatus.PROCESSING_DRUG_CRAWLING:
    case AIReviewStatus.PROCESSING_DRUG_INFO:
    case AIReviewStatus.PROCESSING_PROMPT_CREATION:
    case AIReviewStatus.PROCESSING_LLM_ANALYSIS:
      return "Blue";
    default:
      return "Red";
  }
};

// 사용자 role 라벨 (3글자)
const getRoleLabel = (
  roles: string[] | undefined
): { label: string; color: ColorSystem } | null => {
  if (!roles || roles.length === 0) return null;

  if (roles.includes(UserRole.ADMIN)) return { label: "ADM", color: "Gray" as ColorSystem };
  if (roles.includes(UserRole.GYEONGGI_PHARMACIST))
    return { label: "경기도", color: "Blue" as ColorSystem };

  // 서울시약사회 권한 체크 (Separator 방식 포함: "서울시약사회" 또는 "서울시약사회/구로구")
  const hasSeoulRole = roles.some(
    (role) => role === UserRole.SEOUL_PHARMACIST || role.startsWith(UserRole.SEOUL_PHARMACIST + "/")
  );
  if (hasSeoulRole) return { label: "서울시", color: "Purple" as ColorSystem };

  // 인천시약사회 권한 체크 (Separator 방식 포함: "인천시약사회" 또는 "인천시약사회/남동구")
  const hasIncheonRole = roles.some(
    (role) => role === UserRole.INCHEON_PHARMACIST || role.startsWith(UserRole.INCHEON_PHARMACIST + "/")
  );
  if (hasIncheonRole) return { label: "인천시", color: "Skyblue" as ColorSystem };

  return null; // user role은 표시하지 않음
};

// Premium Role 배지 표시 (값이 있으면 무조건 표시)
const getPremiumRoleBadge = (
  premiumRole: string | null | undefined
): { label: string; color: ColorSystem } | null => {
  if (!premiumRole) return null;

  // "유료프리미엄" → "유료 프리미엄"
  if (premiumRole === "유료프리미엄") {
    return { label: "유료 프리미엄", color: "Red" as ColorSystem };
  }

  // "admin" → "ADM"
  if (premiumRole === UserRole.ADMIN || premiumRole === "admin") {
    return { label: "ADM", color: "Red" as ColorSystem };
  }

  // "경기도약사회" 또는 "경기도약사회/..." → "경기도"
  if (premiumRole.startsWith(UserRole.GYEONGGI_PHARMACIST) || premiumRole.startsWith("경기도약사회")) {
    return { label: "경기도", color: "Blue" as ColorSystem };
  }

  // "서울시약사회" 또는 "서울시약사회/..." → "서울시"
  if (premiumRole.startsWith(UserRole.SEOUL_PHARMACIST) || premiumRole.startsWith("서울시약사회")) {
    return { label: "서울시", color: "Purple" as ColorSystem };
  }

  // "인천시약사회" 또는 "인천시약사회/..." → "인천시"
  if (premiumRole.startsWith(UserRole.INCHEON_PHARMACIST) || premiumRole.startsWith("인천시약사회")) {
    return { label: "인천시", color: "Skyblue" as ColorSystem };
  }

  // "Premium" → "Premium" (그대로)
  if (premiumRole === "Premium") {
    return { label: "Premium", color: "Orange" as ColorSystem };
  }

  // 기타 프리미엄 권한 → 원본 그대로 표시
  return { label: premiumRole, color: "Purple" as ColorSystem };
};

// 보고서 종류 표시
const getReviewType = (review: any): string => {
  // request.promptId가 있으면 promptId 표시
  if (review.request?.promptId) {
    return `프롬프트 #${review.request.promptId}`;
  }
  // promptType에 따라 한글명 표시
  if (review.promptType) {
    const { getPromptTypeKoreanName } = require("../../../common/models/types");
    return getPromptTypeKoreanName(review.promptType);
  }
  return "일반";
};

// 권한 원문 복사
const copyRole = (role: string) => {
  navigator.clipboard.writeText(role);
  ToastManager.show({ content: `권한이 클립보드에 복사되었습니다: ${role}`, duration: 2000 });
};

// 사용자 roles 배열에서 표시된 권한의 원본 문자열 찾기
const findOriginalRole = (roles: string[] | undefined, displayedRole: "admin" | "경기도" | "서울시" | "인천시"): string | null => {
  if (!roles || roles.length === 0) return null;

  if (displayedRole === "admin") {
    return roles.includes(UserRole.ADMIN) ? UserRole.ADMIN : null;
  }

  if (displayedRole === "경기도") {
    return roles.find(role =>
      role === UserRole.GYEONGGI_PHARMACIST || role.startsWith(UserRole.GYEONGGI_PHARMACIST + "/")
    ) || null;
  }

  if (displayedRole === "서울시") {
    return roles.find(role =>
      role === UserRole.SEOUL_PHARMACIST || role.startsWith(UserRole.SEOUL_PHARMACIST + "/")
    ) || null;
  }

  if (displayedRole === "인천시") {
    return roles.find(role =>
      role === UserRole.INCHEON_PHARMACIST || role.startsWith(UserRole.INCHEON_PHARMACIST + "/")
    ) || null;
  }

  return null;
};

export const AdminAIReviewTable: React.FC<AdminAIReviewTableProps> = ({
  reviews,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onRefund,
  onViewDetail,
  selectedIds = new Set(),
  onSelectReview,
  showCheckbox = false,
  onRowDoubleClick,
}) => {
  const totalPages = Math.ceil(total / pageSize);

  // 디버깅을 위한 로그 추가
  React.useEffect(() => {
    if (reviews.length > 0) {
      console.log("AdminAIReviewTable - First review data:", reviews[0]);
      console.log("Analysis data:", reviews[0]?.analysis);
      console.log("Timings data:", reviews[0]?.analysis?.timings);
      console.log("LIFE data:", reviews[0]?.analysis?.timings?.LIFE);
    }
  }, [reviews]);

  // 페이지 번호 생성
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading && reviews.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>데이터를 불러오는 중...</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  if (reviews.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>조회된 AI 리뷰가 없습니다.</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            {showCheckbox && <Th $withCheckbox={showCheckbox}></Th>}
            <Th $withCheckbox={showCheckbox}>번호</Th>
            <Th $withCheckbox={showCheckbox}>환자번호</Th>
            <Th $withCheckbox={showCheckbox}>분석종류</Th>
            <Th $withCheckbox={showCheckbox}>면허번호</Th>
            <Th $withCheckbox={showCheckbox}>사용자</Th>
            <Th $withCheckbox={showCheckbox}>상태</Th>
            <Th $withCheckbox={showCheckbox}>비용</Th>
            <Th $withCheckbox={showCheckbox}>분석 정보</Th>
            <Th $withCheckbox={showCheckbox}>생성일</Th>
            <Th $withCheckbox={showCheckbox}>액션</Th>
          </Tr>
        </Thead>
        <Tbody>
          {reviews.map((review) => {
            // 완료/실패 상태가 아닌 경우에만 더블클릭 가능
            const canDoubleClick = onRowDoubleClick &&
              review.status !== AIReviewStatus.COMPLETED &&
              review.status !== AIReviewStatus.FAILED;

            return (
            <Tr
              key={review.id}
              onDoubleClick={canDoubleClick ? () => onRowDoubleClick(review) : undefined}
              style={{ cursor: canDoubleClick ? 'pointer' : undefined }}
            >
              {showCheckbox && (
                <CheckboxCell>
                  <Checkbox
                    type="checkbox"
                    checked={selectedIds.has(review.id)}
                    onChange={(e) => onSelectReview?.(review.id, e.target.checked)}
                  />
                </CheckboxCell>
              )}
              <Td>{review.id}</Td>
              <Td>{review.patientId || "-"}</Td>
              <Td>
                <Tag variant="Tinted" color="Blue" size="Small">
                  {getReviewType(review)}
                </Tag>
              </Td>
              <Td>
                <Text textType="Caption" color="#6b7280">
                  {review.user?.license || "-"}
                </Text>
              </Td>
              <Td>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {review.user?.name && (
                    <Text textType="Caption" color="#6b7280">
                      {review.user.name}
                    </Text>
                  )}
                  {getPremiumRoleBadge(review.premiumRole) && (
                    <ClickableTag onClick={() => review.premiumRole && copyRole(review.premiumRole)}>
                      <Tag
                        variant="Outlined"
                        color={getPremiumRoleBadge(review.premiumRole).color}
                        size="Small"
                      >
                        {getPremiumRoleBadge(review.premiumRole).label}
                      </Tag>
                    </ClickableTag>
                  )}
                </div>
              </Td>
              <Td>
                <StatusTags>
                  <Tag variant="Tinted" color={getStatusTagColor(review.status)} size="Small">
                    {getStatusLabel(review.status)}
                  </Tag>
                  {review.analysis?.refund && (
                    <Tag variant="Tinted" color="Red" size="Small">
                      환불됨
                    </Tag>
                  )}
                  {review.evaluationId && (
                    <Tag variant="Tinted" color="Green" size="Small">
                      평가
                    </Tag>
                  )}
                  {review.deletedAt && (
                    <Tag variant="Tinted" color="Gray" size="Small">
                      삭제
                    </Tag>
                  )}
                </StatusTags>
              </Td>
              <Td>${review.analysis?.cost?.toLocaleString() || 0}</Td>
              <Td>
                <InfoWrapper>
                  <InfoItem>
                    <Text textType="Caption">시간:</Text>
                    <Text textType="Caption">
                      {formatDuration(review.analysis?.timings?.LIFE?.durationMs)}
                    </Text>
                  </InfoItem>
                  <InfoItem>
                    <Text textType="Caption">처방전:</Text>
                    <Text textType="Caption">
                      {review.analysis?.initial?.prescriptionCount || 0}개
                    </Text>
                  </InfoItem>
                  <InfoItem>
                    <Text textType="Caption">의약품:</Text>
                    <Text textType="Caption">
                      {review.analysis?.initial?.totalMedicines || 0}개 (
                      {review.analysis?.initial?.uniqueMedicines || 0}개)
                    </Text>
                  </InfoItem>
                </InfoWrapper>
              </Td>
              <Td>{new Date(review.createdAt).toLocaleDateString("ko-KR")}</Td>
              <Td>
                <ActionButtons>
                  <Button
                    size="Small"
                    type="Text"
                    variant="Contained"
                    color="Primary"
                    onClick={() => onViewDetail(review.id)}
                  >
                    상세
                  </Button>
                  {review.status === AIReviewStatus.COMPLETED &&
                   !review.analysis?.refund &&
                   review.pointsTransactionId && (
                    <Button
                      size="Small"
                      type="Text"
                      variant="Outlined"
                      color="Secondary"
                      onClick={() => onRefund(review)}
                    >
                      환불
                    </Button>
                  )}
                </ActionButtons>
              </Td>
            </Tr>
            );
          })}
        </Tbody>
      </Table>

      {totalPages > 1 && (
        <PaginationContainer>
          <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            이전
          </PageButton>

          {getPageNumbers().map((page) => (
            <PageButton key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
              {page}
            </PageButton>
          ))}

          <PageInfo>
            {currentPage} / {totalPages} 페이지
          </PageInfo>

          <PageButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            다음
          </PageButton>
        </PaginationContainer>
      )}
    </TableContainer>
  );
};
